import * as braze from '@braze/web-sdk';
import { getTokens } from '@rb-serve-app/core';

export interface BrazeWebConfig {
  apiKey: string;
  endpoint: string;
  enableLogging?: boolean;
  enableSdkAuthentication?: boolean;
  manageServiceWorkerExternally?: boolean;
  safariWebsitePushId?: string;
  serviceWorkerLocation?: string;
}

export interface BrazeInitInput {
  env: string;
  brazeWeb?: BrazeWebConfig;
}

let initialized = false;
let currentUserId: string | null = null;
let inAppMessageSubscriptionId: string | undefined;

const DEFAULT_IN_APP_MESSAGE_TIMEOUT_MS = 2000;

type InAppMessageLifecycle = 'IAM_OPENED' | 'IAM_CLOSED' | 'IAM_COMPLETED';
type InAppMessageHandler = (
  event: InAppMessageLifecycle,
  extras: Record<string, string>
) => void;

let inAppMessageHandler: InAppMessageHandler | null = null;
let inAppMessageListenerEnabled = false;
let hasInAppMessageShown = false;
let hasInitialInAppMessageEventBeenSent = false;
let inAppMessageTimeout: ReturnType<typeof setTimeout> | null = null;
let pushPermissionPromptInFlight = false;

const truthyExtraValues = new Set(['1', 'true', 'yes', 'y']);
const pushPrimingMessageTypes = new Set([
  'push_priming',
  'push-priming',
  'pushpriming',
  'push_permission',
  'push-permission',
  'pushpermission',
]);

const hasTruthyExtra = (value: string | undefined): boolean => {
  if (!value) {
    return false;
  }

  return truthyExtraValues.has(value.trim().toLowerCase());
};

const normalizeExtra = (value: string | undefined): string => {
  return value?.trim().toLowerCase() || '';
};

const getCurrentWebLanguage = (): string => {
  if (typeof localStorage === 'undefined') {
    return 'en';
  }

  return localStorage.getItem('language') === 'fr' ? 'fr' : 'en';
};

const notifyInAppMessage = (
  event: InAppMessageLifecycle,
  inAppMessage: braze.InAppMessage | braze.ControlMessage
): void => {
  if (!inAppMessageHandler) {
    return;
  }

  inAppMessageHandler(event, inAppMessage.extras || {});
};

const cancelInAppMessageTimeout = (): void => {
  if (!inAppMessageTimeout) {
    return;
  }

  clearTimeout(inAppMessageTimeout);
  inAppMessageTimeout = null;
};

const getBrazeWebConfig = (input: BrazeInitInput): BrazeWebConfig | null => {
  const config = input.brazeWeb;

  if (!config?.apiKey || !config?.endpoint) {
    return null;
  }

  return config;
};

const canRequestPushPermission = (): boolean => {
  if (!initialized) {
    return false;
  }

  if (pushPermissionPromptInFlight) {
    return false;
  }

  if (!braze.isPushSupported()) {
    return false;
  }

  if (braze.isPushPermissionGranted()) {
    return false;
  }

  if (
    typeof Notification !== 'undefined' &&
    Notification.permission === 'denied'
  ) {
    return false;
  }

  return true;
};

const enableBrazeInAppMessaging = (): void => {
  if (inAppMessageSubscriptionId) {
    return;
  }

  // Show every IAM via Braze renderer and trigger push-priming logic only for matching IAM payloads.
  inAppMessageSubscriptionId = braze.subscribeToInAppMessage((inAppMessage) => {
    // Temporary debug logging to verify Braze IAM delivery and payload contents.
    console.log('[Braze][IAM][payload]', {
      isControl: inAppMessage.isControl,
      triggerId: inAppMessage.triggerId,
      extras: inAppMessage.extras || {},
      message: 'message' in inAppMessage ? inAppMessage.message : undefined,
    });

    if (inAppMessageListenerEnabled) {
      hasInAppMessageShown = true;
      hasInitialInAppMessageEventBeenSent = true;
      cancelInAppMessageTimeout();
      notifyInAppMessage('IAM_OPENED', inAppMessage);

      if (!inAppMessage.isControl) {
        inAppMessage.subscribeToDismissedEvent(() => {
          notifyInAppMessage('IAM_CLOSED', inAppMessage);
        });
      }
    }

    braze.showInAppMessage(inAppMessage);
  });
};

export const setInAppMessageHandler = (handler: InAppMessageHandler): void => {
  inAppMessageHandler = handler;
};

export const initializeInAppMessageListener = (): void => {
  inAppMessageListenerEnabled = true;
};

export const startInAppMessageTimeout = (
  timeoutMs = DEFAULT_IN_APP_MESSAGE_TIMEOUT_MS
): void => {
  cancelInAppMessageTimeout();

  inAppMessageTimeout = setTimeout(() => {
    if (!inAppMessageListenerEnabled) {
      return;
    }

    if (!hasInAppMessageShown && !hasInitialInAppMessageEventBeenSent) {
      hasInitialInAppMessageEventBeenSent = true;
      if (inAppMessageHandler) {
        inAppMessageHandler('IAM_COMPLETED', {});
      }
    }
  }, timeoutMs);
};

export const requestBrazeDataFlush = (): boolean => {
  if (!initialized) {
    return false;
  }

  try {
    braze.requestImmediateDataFlush();
    return true;
  } catch (error) {
    console.error('[Braze] data flush request failed:', error);
    return false;
  }
};

export const requestBrazePushPermission = (): boolean => {
  if (!canRequestPushPermission()) {
    return false;
  }

  try {
    pushPermissionPromptInFlight = true;

    braze.requestPushPermission(
      () => {
        pushPermissionPromptInFlight = false;
        if (typeof Notification !== 'undefined') {
          console.log(
            '[Braze] Web push permission result:',
            Notification.permission
          );
        }
      },
      (temporaryDenial) => {
        pushPermissionPromptInFlight = false;
        console.warn(
          `[Braze] Web push permission denied (${temporaryDenial ? 'temporary' : 'permanent'}).`
        );
      }
    );

    return true;
  } catch (error) {
    pushPermissionPromptInFlight = false;
    console.error('[Braze] push permission request failed:', error);
    return false;
  }
};

export const setBrazeAuthenticationSignature = async (): Promise<
  string | null
> => {
  if (!initialized) {
    return null;
  }

  try {
    const tokens = await getTokens();
    const signature = tokens?.brazejwt;

    if (!signature) {
      console.warn('[Braze] SDK authentication signature is missing.');
      return null;
    }

    braze.setSdkAuthenticationSignature(signature);
    return signature;
  } catch (error) {
    console.error('[Braze] SDK authentication signature failed:', error);
    return null;
  }
};

export interface BrazePushDebugStatus {
  initialized: boolean;
  pushSupported: boolean;
  pushPermissionGranted: boolean;
  browserPermission: NotificationPermission | 'unknown';
  serviceWorkerRegistered: boolean;
  serviceWorkerScope: string | null;
  hasPushSubscription: boolean;
  pushEndpoint: string | null;
}

export const getBrazePushDebugStatus =
  async (): Promise<BrazePushDebugStatus> => {
    const pushSupported = Boolean(braze.isPushSupported());
    const pushPermissionGranted = Boolean(braze.isPushPermissionGranted());
    const browserPermission =
      typeof Notification === 'undefined' ? 'unknown' : Notification.permission;

    if (
      typeof navigator === 'undefined' ||
      !('serviceWorker' in navigator) ||
      globalThis.window === undefined
    ) {
      return {
        initialized,
        pushSupported,
        pushPermissionGranted,
        browserPermission,
        serviceWorkerRegistered: false,
        serviceWorkerScope: null,
        hasPushSubscription: false,
        pushEndpoint: null,
      };
    }

    try {
      const serviceWorkerRegistration =
        (await navigator.serviceWorker.getRegistration('/service-worker.js')) ||
        (await navigator.serviceWorker.ready);
      const pushSubscription =
        (await serviceWorkerRegistration?.pushManager.getSubscription()) ||
        null;

      return {
        initialized,
        pushSupported,
        pushPermissionGranted,
        browserPermission,
        serviceWorkerRegistered: Boolean(serviceWorkerRegistration),
        serviceWorkerScope: serviceWorkerRegistration?.scope || null,
        hasPushSubscription: Boolean(pushSubscription),
        pushEndpoint: pushSubscription?.endpoint || null,
      };
    } catch (error) {
      console.error('[Braze] get push debug status failed:', error);
      return {
        initialized,
        pushSupported,
        pushPermissionGranted,
        browserPermission,
        serviceWorkerRegistered: false,
        serviceWorkerScope: null,
        hasPushSubscription: false,
        pushEndpoint: null,
      };
    }
  };

export const maybeRequestBrazePushPermissionFromInAppMessage = (
  event: InAppMessageLifecycle,
  extras: Record<string, string>
): boolean => {
  const pushPermissionFlag =
    extras.pushPermission ||
    extras.push_permission ||
    extras.requestPushPermission ||
    extras.request_push_permission ||
    extras.pushPriming ||
    extras.push_priming;
  const messageType = normalizeExtra(
    extras.messageType || extras.message_type || extras.campaign_type
  );
  const eventOverride = normalizeExtra(
    extras.pushPermissionOn || extras.push_permission_on
  );
  const isPushPrimingMessage =
    hasTruthyExtra(pushPermissionFlag) ||
    pushPrimingMessageTypes.has(messageType);

  if (!isPushPrimingMessage) {
    return false;
  }

  if (eventOverride === 'opened' && event !== 'IAM_OPENED') {
    return false;
  }

  if (eventOverride === 'closed' && event !== 'IAM_CLOSED') {
    return false;
  }

  if (eventOverride === 'completed' && event !== 'IAM_COMPLETED') {
    return false;
  }

  // Default to requesting permission only after the message lifecycle has finished.
  if (!eventOverride && event === 'IAM_OPENED') {
    return false;
  }

  return requestBrazePushPermission();
};

export const disableInAppMessageListener = (): void => {
  inAppMessageListenerEnabled = false;
  cancelInAppMessageTimeout();
};

export const resetInAppMessageListener = (): void => {
  hasInAppMessageShown = false;
  hasInitialInAppMessageEventBeenSent = false;
};

// Backward-compatible aliases while callers migrate from push-priming naming.
export const setPushPrimingHandler = setInAppMessageHandler;
export const initializePushPrimingListener = initializeInAppMessageListener;
export const startPushPrimingTimeout = startInAppMessageTimeout;
export const disablePushPrimingListener = disableInAppMessageListener;
export const resetPushPrimingListener = resetInAppMessageListener;

export const initializeBrazeWeb = (input: BrazeInitInput): boolean => {
  if (initialized) {
    return true;
  }

  const config = getBrazeWebConfig(input);

  if (!config) {
    if (input.env !== 'production') {
      console.warn('[Braze] Missing web config. Braze initialization skipped.');
    }
    return false;
  }

  try {
    const initializationOptions: braze.InitializationOptions = {
      baseUrl: config.endpoint,
      enableLogging: Boolean(config.enableLogging),
      enableSdkAuthentication: config.enableSdkAuthentication ?? true,
      allowUserSuppliedJavascript: true,
      manageServiceWorkerExternally:
        config.manageServiceWorkerExternally ?? false,
      serviceWorkerLocation:
        config.serviceWorkerLocation || '/service-worker.js',
    };

    if (config.safariWebsitePushId) {
      initializationOptions.safariWebsitePushId = config.safariWebsitePushId;
    }

    braze.initialize(config.apiKey, initializationOptions);

    braze.subscribeToSdkAuthenticationFailures(async (event) => {
      console.warn(
        '[Braze] SDK auth failure, refreshing signature:',
        event.errorCode
      );
      await setBrazeAuthenticationSignature();
    });

    enableBrazeInAppMessaging();
    initialized = true;

    if (input.env !== 'production') {
      console.log(`[Braze] Initialized for environment: ${input.env}`);
    }

    return true;
  } catch (error) {
    console.error('[Braze] Initialization failed:', error);
    return false;
  }
};

export const isBrazeReady = (): boolean => {
  return initialized;
};

export const identifyBrazeUser = (
  userId: string,
  attributes?: Record<string, string | number | boolean | null | undefined>,
  signature?: string
): boolean => {
  if (!initialized || !userId) {
    return false;
  }

  try {
    if (currentUserId !== userId) {
      braze.changeUser(userId, signature);
      currentUserId = userId;
    }

    if (attributes) {
      Object.entries(attributes).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          braze.getUser()?.setCustomUserAttribute(key, value);
        }
      });
    }

    return true;
  } catch (error) {
    console.error('[Braze] identify user failed:', error);
    return false;
  }
};

export const cleanupBrazeOnLogout = (): boolean => {
  if (!initialized) {
    return false;
  }

  try {
    currentUserId = null;
    pushPermissionPromptInFlight = false;
    disableInAppMessageListener();
    resetInAppMessageListener();
    return true;
  } catch (error) {
    console.error('[Braze] cleanup on logout failed:', error);
    return false;
  }
};

export const logBrazeCustomEvent = (
  eventName: string,
  properties?: Record<string, string | number | boolean | null | undefined>
): boolean => {
  if (!initialized || !eventName) {
    return false;
  }

  try {
    const sanitized = properties
      ? Object.entries(properties).reduce(
          (acc, [key, value]) => {
            if (value !== null && value !== undefined && value !== '') {
              acc[key] = value;
            }
            return acc;
          },
          {} as Record<string, string | number | boolean>
        )
      : {};

    braze.logCustomEvent(eventName, {
      ...sanitized,
      lang: getCurrentWebLanguage(),
    });
    return true;
  } catch (error) {
    console.error('[Braze] log custom event failed:', error);
    return false;
  }
};
